import type { MaybeRef } from 'vue';
import { toPlainText } from '@portabletext/toolkit';
import { OBJECT } from '@pkg/config/sanity/schemas';
import {
  injectJsonLdScript,
  useJsonLdShared,
} from './useJsonLdShared';

interface HowToSlide {
  content?: Array<{
    _type?: string;
    title?: unknown;
    preTitle?: unknown;
    text?: unknown;
  }>;
}

interface HowToPage {
  slug?: string;
  title?: string;
  seoSocial?: { metaTitle?: string; metaDescription?: string };
  content?: Array<{
    _type?: string;
    slides?: HowToSlide[];
    content?: Array<{ _type?: string; items?: unknown }>;
  }>;
}

function isHowToSlug(slug: unknown): boolean {
  if (typeof slug !== 'string' || !slug) {
    return false;
  }
  return slug
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase()
    .split('/')
    .some(part => part.startsWith('how-to'));
}

function portableToText(value: unknown): string {
  if (!value) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (Array.isArray(value)) {
    try {
      return toPlainText(value as Parameters<typeof toPlainText>[0]).trim();
    } catch {
      return '';
    }
  }
  return '';
}

function stepFromSlide(slide: HowToSlide, index: number) {
  const blocks = slide.content || [];
  const titleBlock = blocks.find(b => b._type === OBJECT.MODULAR_INNER_BLOCK_TITLE);
  const textBlock = blocks.find(b => b._type === OBJECT.MODULAR_INNER_BLOCK_BASE_TEXT);

  const preTitle = portableToText(titleBlock?.preTitle);
  const title = portableToText(titleBlock?.title);
  const name = [preTitle, title].filter(Boolean).join(' - ') || `Step ${index + 1}`;
  const text = portableToText(textBlock?.text);

  return {
    name,
    ...(text && { text }),
  };
}

function findStepsSection(content: HowToPage['content']) {
  return (content || []).find(
    block => block._type === OBJECT.MODULAR_OUTER_BLOCK_STEPS_SECTION,
  );
}

function pageHasFaqBlock(content: HowToPage['content']): boolean {
  for (const block of content || []) {
    if (block._type === OBJECT.MODULAR_INNER_BLOCK_FAQ) {
      return true;
    }
    for (const inner of block.content || []) {
      if (inner._type === OBJECT.MODULAR_INNER_BLOCK_FAQ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * HowTo + WebPage JSON-LD for How To pages only.
 * Gated by slug (`how-to*`) + presence of stepsSection — avoids marking generic sliders as HowTo
 * until non–How To stepsSection usages are migrated.
 */
export function useHowToJsonLd(pageData: MaybeRef<{ page?: HowToPage } | null>) {
  const { localePath, baseUrl } = useJsonLdShared();

  const jsonLd = computed(() => {
    const page = unref(pageData)?.page;
    if (!page || !isHowToSlug(page.slug)) {
      return null;
    }

    const stepsSection = findStepsSection(page.content);
    const slides = stepsSection?.slides || [];
    if (!slides.length) {
      return null;
    }

    const steps = slides
      .map((slide, index) => stepFromSlide(slide, index))
      .filter(step => step.name);

    if (!steps.length) {
      return null;
    }

    if (typeof page.slug !== 'string') {
      return null;
    }
    const slugPath = page.slug.startsWith('/') ? page.slug : `/${page.slug}`;
    const pageUrl = `${baseUrl.value}${localePath(slugPath)}`;
    const name = page.seoSocial?.metaTitle || page.title || 'How To';
    const description = page.seoSocial?.metaDescription || '';

    const webpageId = `${pageUrl}#webpage`;
    const howToId = `${pageUrl}#howto`;
    const faqId = `${pageUrl}#faq`;
    const hasFaq = pageHasFaqBlock(page.content);

    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': webpageId,
          url: pageUrl,
          name,
          ...(description && { description }),
          about: [
            { '@id': howToId },
            ...(hasFaq ? [{ '@id': faqId }] : []),
          ],
        },
        {
          '@type': 'HowTo',
          '@id': howToId,
          name,
          ...(description && { description }),
          step: steps.map((step, index) => ({
            '@type': 'HowToStep',
            '@id': `${pageUrl}#step${index + 1}`,
            position: index + 1,
            name: step.name,
            ...(step.text && { text: step.text }),
            url: `${pageUrl}#step${index + 1}`,
          })),
        },
      ],
    };
  });

  injectJsonLdScript('howto-jsonld', jsonLd);
}
